import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, Link } from 'react-router';
import { register, clearError } from '../../store/slices/authSlice';
import { trackSignupStarted } from '../../utils/analytics';
import LocationPicker from '../../components/common/LocationPicker';
import authAPI, { validatePassword, formatRegistrationError } from '../../api/authAPI';
import { formatUserFacingError } from '../../utils/formatUserFacingError';
import { sendOtpReliable } from '../../utils/otpSendRequest';
import { REFERRAL_REWARD_EGP } from '../../constants/referralProgram';
import { markOnboardingPending } from '../../utils/onboardingStorage';
import { buildLoginPath, resolveReturnPath } from '../../utils/authRedirect';
import {
  buildFullPhoneNumber,
  cleanPhoneDigits,
  getEgyptianMobileValidationError,
  getPhoneValidationError,
  isValidPhoneForCountry,
} from '../../utils/egyptianPhoneValidation';
import {
  normalizeOtpDeliveryChannel,
  otpSendPrompt,
  otpSentPrompt,
  otpEnterHint,
  formatOtpVerifyError,
  OTP_DELIVERY_NOTICE,
  OTP_RESEND_NOTICE,
  OTP_PASTE_EMPTY,
  OTP_SMS_ENTER_HINT,
} from '../../utils/otpDelivery';
import { extractOtpFromPastedText, sanitizeOtpInput } from '../../utils/sanitizeText';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const countryCodes = [
  { code: '+20', flag: '🇪🇬' },
  { code: '+1', flag: '🇺🇸' },
  { code: '+44', flag: '🇬🇧' },
  { code: '+971', flag: '🇦🇪' },
  { code: '+966', flag: '🇸🇦' },
  { code: '+974', flag: '🇶🇦' },
  { code: '+965', flag: '🇰🇼' },
  { code: '+973', flag: '🇧🇭' },
  { code: '+968', flag: '🇴🇲' },
  { code: '+961', flag: '🇱🇧' },
  { code: '+962', flag: '🇯🇴' },
  { code: '+212', flag: '🇲🇦' },
  { code: '+213', flag: '🇩🇿' },
  { code: '+216', flag: '🇹🇳' },
  { code: '+249', flag: '🇸🇩' },
  { code: '+27', flag: '🇿🇦' },
  { code: '+33', flag: '🇫🇷' },
  { code: '+49', flag: '🇩🇪' },
  { code: '+39', flag: '🇮🇹' },
  { code: '+34', flag: '🇪🇸' },
  { code: '+90', flag: '🇹🇷' },
  { code: '+91', flag: '🇮🇳' },
  { code: '+86', flag: '🇨🇳' },
  { code: '+81', flag: '🇯🇵' },
  { code: '+82', flag: '🇰🇷' },
  { code: '+61', flag: '🇦🇺' },
  { code: '+64', flag: '🇳🇿' },
  { code: '+55', flag: '🇧🇷' },
  { code: '+52', flag: '🇲🇽' },
  { code: '+7', flag: '🇷🇺' },
];

const RegisterPage = () => {
  const [step, setStep] = useState('phone');
  const [countryCode, setCountryCode] = useState('+20');
  const [phoneDigits, setPhoneDigits] = useState('');
  const [verifiedPhoneNumber, setVerifiedPhoneNumber] = useState('');
  const [otpDeliveryChannel, setOtpDeliveryChannel] = useState('sms');

  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [sendingOTP, setSendingOTP] = useState(false);
  const [verifyingOTP, setVerifyingOTP] = useState(false);
  const [otpError, setOtpError] = useState(null);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [otpVerifyFailed, setOtpVerifyFailed] = useState(false);
  const otpInputRefs = useRef([]);
  const verifyingOtpRef = useRef(false);
  const otpCountdownTimerRef = useRef(null);

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    shipping_address: '',
    referral_code: '',
    latitude: undefined,
    longitude: undefined,
  });
  const [passwordError, setPasswordError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showReferralField, setShowReferralField] = useState(false);

  const [userExists, setUserExists] = useState(false);
  const [userExistsMessage, setUserExistsMessage] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const userCheckTimeoutRef = useRef(null);

  const [phoneUserExists, setPhoneUserExists] = useState(false);
  const [phoneUserExistsMessage, setPhoneUserExistsMessage] = useState(null);
  const [checkingPhone, setCheckingPhone] = useState(false);
  const [phoneCheckComplete, setPhoneCheckComplete] = useState(false);
  const phoneCheckTimeoutRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { loading, error, isAuthenticated } = useSelector((state) => state.auth);
  const returnTo = resolveReturnPath({ search: location.search, state: location.state });
  const params = new URLSearchParams(location.search);
  const requireAuth =
    params.get('continue') === '1' || Boolean(location.state?.requireAuth);
  const loginHref = buildLoginPath(returnTo);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(returnTo, { replace: true });
    }
  }, [isAuthenticated, navigate, returnTo]);

  useEffect(() => {
    trackSignupStarted();
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (ref) {
      setFormData((prev) => ({ ...prev, referral_code: ref.trim().toUpperCase() }));
      setShowReferralField(true);
    }
  }, []);

  useEffect(() => {
    if (!error) return;
    const errorMessage = formatRegistrationError(error, formatUserFacingError(error, 'Registration failed'));
    const looksLikeExistingAccount = /already (exists|registered|in use|taken)|account with this/i.test(
      errorMessage,
    );
    if (looksLikeExistingAccount) {
      setUserExists(true);
      setUserExistsMessage(
        /email/i.test(errorMessage)
          ? 'An account with this email already exists. Sign in instead.'
          : /phone/i.test(errorMessage)
            ? 'An account with this phone number already exists. Sign in instead.'
            : errorMessage,
      );
    }
    toast.error(errorMessage);
    if (/password/i.test(errorMessage)) {
      setPasswordError(errorMessage);
    }
    dispatch(clearError());
  }, [error, dispatch]);

  useEffect(() => {
    return () => {
      if (userCheckTimeoutRef.current) clearTimeout(userCheckTimeoutRef.current);
      if (phoneCheckTimeoutRef.current) clearTimeout(phoneCheckTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (step !== 'phone') {
      setPhoneUserExists(false);
      setPhoneUserExistsMessage(null);
      setCheckingPhone(false);
      setPhoneCheckComplete(false);
      return;
    }

    if (phoneCheckTimeoutRef.current) clearTimeout(phoneCheckTimeoutRef.current);

    const digits = phoneDigits.trim();
    const isValidFormat = isValidPhoneForCountry(countryCode, digits);
    if (!isValidFormat) {
      setPhoneUserExists(false);
      setPhoneUserExistsMessage(null);
      setCheckingPhone(false);
      setPhoneCheckComplete(false);
      return;
    }

    const fullPhoneNumber = buildFullPhoneNumber(countryCode, digits);
    if (!fullPhoneNumber) {
      setPhoneUserExists(false);
      setPhoneUserExistsMessage(null);
      setCheckingPhone(false);
      setPhoneCheckComplete(false);
      return;
    }

    setCheckingPhone(true);
    setPhoneCheckComplete(false);
    setPhoneUserExists(false);
    setPhoneUserExistsMessage(null);

    phoneCheckTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await authAPI.checkUserExists(null, fullPhoneNumber);
        const exists = Boolean(result.exists);
        setPhoneUserExists(exists);
        setPhoneUserExistsMessage(
          exists
            ? (result.message || 'An account with this phone number already exists. Sign in instead.')
            : null,
        );
      } catch {
        setPhoneUserExists(false);
        setPhoneUserExistsMessage(null);
      } finally {
        setCheckingPhone(false);
        setPhoneCheckComplete(true);
      }
    }, 500);

    return () => {
      if (phoneCheckTimeoutRef.current) clearTimeout(phoneCheckTimeoutRef.current);
    };
  }, [phoneDigits, countryCode, step]);

  useEffect(() => {
    if (step !== 'register') {
      setUserExists(false);
      setUserExistsMessage(null);
      return;
    }
    if (userCheckTimeoutRef.current) clearTimeout(userCheckTimeoutRef.current);

    if (!verifiedPhoneNumber) {
      setUserExists(false);
      setUserExistsMessage(null);
      return;
    }

    userCheckTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await authAPI.checkUserExists(
          formData.email?.trim() || null,
          verifiedPhoneNumber || null,
        );
        const exists = Boolean(result.exists);
        setUserExists(exists);
        setUserExistsMessage(
          exists
            ? (result.message || 'An account with this phone number already exists. Sign in instead.')
            : null,
        );
      } catch {
        setUserExists(false);
        setUserExistsMessage(null);
      }
    }, 500);

    return () => {
      if (userCheckTimeoutRef.current) clearTimeout(userCheckTimeoutRef.current);
    };
  }, [verifiedPhoneNumber, step, formData.email]);

  const startOtpCountdown = useCallback(() => {
    if (otpCountdownTimerRef.current) {
      clearInterval(otpCountdownTimerRef.current);
    }
    setOtpCountdown(60);
    otpCountdownTimerRef.current = setInterval(() => {
      setOtpCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(otpCountdownTimerRef.current);
          otpCountdownTimerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleSendOTP = async ({ preferSms = false } = {}) => {
    const digits = phoneDigits.trim();
    const validationError = getPhoneValidationError(countryCode, digits);
    if (validationError) {
      setOtpError(validationError);
      return;
    }
    if (phoneUserExists) {
      return;
    }

    const fullPhoneNumber = buildFullPhoneNumber(countryCode, digits);
    if (!fullPhoneNumber) {
      setOtpError(getPhoneValidationError(countryCode, digits) || 'Invalid phone number');
      return;
    }
    setSendingOTP(true);
    setOtpError(null);

    try {
      const response = await sendOtpReliable(fullPhoneNumber, 'registration', { preferSms });
      setOtpDeliveryChannel(normalizeOtpDeliveryChannel(response?.delivery_channel));
      toast.info(preferSms ? OTP_SMS_ENTER_HINT : step === 'otp' ? OTP_RESEND_NOTICE : OTP_DELIVERY_NOTICE, {
        autoClose: 6000,
      });
      setVerifiedPhoneNumber(fullPhoneNumber);
      setOtpDigits(['', '', '', '', '', '']);
      setStep('otp');
      startOtpCountdown();
      setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
    } catch (err) {
      const raw = err.response?.data?.error;
      setOtpError(
        typeof raw === 'string' && raw.trim()
          ? raw
          : formatUserFacingError(err, "Couldn't send the verification code. Stay on this page and try again.")
      );
    } finally {
      setSendingOTP(false);
    }
  };

  const fillOtpDigits = (code) => {
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < code.length && i < 6; i += 1) {
      next[i] = code[i];
    }
    setOtpDigits(next);
    return next.join('');
  };

  const handleVerifyOTP = async (codeOverride) => {
    const code = sanitizeOtpInput(codeOverride || otpDigits.join(''));
    if (code.length !== 6) {
      setOtpError('Please enter the complete 6-digit code');
      return;
    }
    if (verifyingOtpRef.current) {
      return;
    }

    verifyingOtpRef.current = true;
    setVerifyingOTP(true);
    setOtpError(null);

    try {
      const response = await authAPI.verifyOTP(verifiedPhoneNumber, code, 'registration');
      if (response.verified) {
        setStep('register');
        setOtpError(null);
      } else {
        setOtpVerifyFailed(true);
        setOtpError(formatOtpVerifyError(response.error || "That code didn't match. Copy the code from the latest message."));
        setOtpDigits(['', '', '', '', '', '']);
        otpInputRefs.current[0]?.focus();
      }
    } catch (err) {
      const raw = err.response?.data?.error;
      let msg =
        typeof raw === 'string' && raw.trim()
          ? raw
          : formatUserFacingError(err, 'Failed to verify code');
      setOtpVerifyFailed(true);
      setOtpError(formatOtpVerifyError(msg, err.response?.data?.attempts_remaining));
      setOtpDigits(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } finally {
      verifyingOtpRef.current = false;
      setVerifyingOTP(false);
    }
  };

  const applyPastedOtp = (raw) => {
    const digits = extractOtpFromPastedText(raw);
    if (digits.length === 6) {
      fillOtpDigits(digits);
      handleVerifyOTP(digits);
      return true;
    }
    return false;
  };

  const handlePasteOtp = async () => {
    try {
      const raw = await navigator.clipboard.readText();
      if (!applyPastedOtp(raw)) {
        setOtpError(OTP_PASTE_EMPTY);
      }
    } catch {
      setOtpError('Allow clipboard access, or paste into the first box.');
    }
  };

  const handleOtpDigitChange = (index, value) => {
    if (applyPastedOtp(value)) {
      return;
    }
    const digits = extractOtpFromPastedText(value);
    if (!digits) {
      if (!value) {
        const cleared = [...otpDigits];
        cleared[index] = '';
        setOtpDigits(cleared);
      }
      return;
    }
    const next = [...otpDigits];
    for (let i = 0; i < digits.length && index + i < 6; i += 1) {
      next[index + i] = digits[i];
    }
    setOtpDigits(next);
    setOtpError(null);
    const lastFilled = Math.min(index + digits.length - 1, 5);
    if (lastFilled < 5) {
      otpInputRefs.current[lastFilled + 1]?.focus();
    }
    if (next.join('').length === 6) handleVerifyOTP(next.join(''));
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const usernameFromEmail = (email) => {
    const local = String(email || '').trim().toLowerCase().split('@')[0] || 'user';
    const base = local.replace(/[^a-z0-9_]/g, '').slice(0, 16) || 'user';
    const suffix = String(Date.now() % 100000).padStart(4, '0');
    return `${base}${suffix}`.slice(0, 30);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'referral_code'
      ? value.toUpperCase().replace(/[^A-Z0-9]/g, '')
      : value;
    setFormError(null);
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    if (name === 'password') {
      setPasswordError(null);
    }
  };

  const handleCompleteRegistration = async (e) => {
    e.preventDefault();
    if (step !== 'register' || userExists) return;
    setFormError(null);

    const emailValue = String(formData.email || '')
      .replace(/\uFF20/g, '@')
      .replace(/＠/g, '@')
      .replace(/[\u200B-\u200D\uFEFF\u00A0]/g, '')
      .trim();

    if (
      !formData.full_name?.trim()
      || !emailValue
      || !formData.password
      || !verifiedPhoneNumber
    ) {
      setFormError(t('auth.registerErrors.fillRequired'));
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
      setFormError(t('auth.registerErrors.invalidEmail'));
      return;
    }

    if (!acceptedTerms) {
      setFormError(t('auth.registerErrors.acceptTerms'));
      return;
    }

    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      setPasswordError(t('auth.registerErrors.passwordTooShort'));
      return;
    }

    const nameParts = formData.full_name.trim().split(/\s+/);
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ') || nameParts[0];

    try {
      const result = await dispatch(
        register({
          username: usernameFromEmail(emailValue),
          email: emailValue.toLowerCase(),
          password: formData.password,
          first_name: firstName,
          last_name: lastName,
          phone_number: verifiedPhoneNumber,
          shipping_address: formData.shipping_address,
          referral_code: formData.referral_code.trim() || undefined,
          ...(formData.latitude != null && formData.longitude != null
            ? { latitude: formData.latitude, longitude: formData.longitude }
            : {}),
        }),
      ).unwrap();

      if (result?.referral_bonus_awarded) {
        toast.success(
          `Welcome to Flea! Your friend will receive ${REFERRAL_REWARD_EGP} EGP in their wallet.`,
        );
      }
      markOnboardingPending();
    } catch {
      // Error surfaced via auth slice useEffect
    }
  };

  const goBackToPhone = () => {
    setStep('phone');
    setOtpDigits(['', '', '', '', '', '']);
    setOtpError(null);
    setOtpCountdown(0);
    setOtpVerifyFailed(false);
  };

  const phoneDigitsValid = isValidPhoneForCountry(countryCode, phoneDigits.trim());
  const cleanedPhoneDigits = cleanPhoneDigits(phoneDigits);
  const phoneFormatError = (() => {
    if (!cleanedPhoneDigits) return null;
    if (countryCode === '+20') {
      if (cleanedPhoneDigits.length > 11) {
        return getEgyptianMobileValidationError(phoneDigits);
      }
      if (cleanedPhoneDigits.length >= 3 && !cleanedPhoneDigits.startsWith('01')) {
        return getEgyptianMobileValidationError(phoneDigits);
      }
      if (cleanedPhoneDigits.length === 11) {
        return getEgyptianMobileValidationError(phoneDigits);
      }
      return null;
    }
    return cleanedPhoneDigits.length >= 8 ? getPhoneValidationError(countryCode, phoneDigits) : null;
  })();
  const canSendOtp =
    phoneDigitsValid &&
    phoneCheckComplete &&
    !checkingPhone &&
    !phoneUserExists &&
    !sendingOTP;

  return (
    <Container>
      <FormContainer>
        <Title>
          {step === 'phone' && 'Create Account'}
          {step === 'otp' && 'Enter Verification Code'}
          {step === 'register' && 'Create Account'}
        </Title>
        <Subtitle>
          {requireAuth && step === 'phone' && (
            <>
              Create an account to continue
              <br />
            </>
          )}
          {step === 'phone' && otpSendPrompt(otpDeliveryChannel)}
          {step === 'otp' && (
            <>
              {otpSentPrompt(otpDeliveryChannel)}<br />
              <PhoneHighlight>{verifiedPhoneNumber}</PhoneHighlight>
            </>
          )}
          {step === 'register' && 'Almost done — tell us a bit about you'}
        </Subtitle>
        {requireAuth && (
          <ContinueBanner>
            Already have an account?{' '}
            <ContinueBannerLink to={loginHref}>Sign In</ContinueBannerLink>
          </ContinueBanner>
        )}

        {step === 'phone' && (
          <Form onSubmit={(e) => { e.preventDefault(); handleSendOTP(); }}>
            <PhoneNumberSection>
              <PhoneNumberLabel>Phone Number *</PhoneNumberLabel>
              <PhoneNumberWrapper>
                <CountryCodeSelect
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                >
                  {countryCodes.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {c.code}
                    </option>
                  ))}
                </CountryCodeSelect>
                <PhoneInput
                  type="tel"
                  placeholder="Phone number"
                  value={phoneDigits}
                  onChange={(e) => {
                    setPhoneDigits(e.target.value.replace(/[^\d]/g, ''));
                    setOtpError(null);
                    setPhoneCheckComplete(false);
                  }}
                  inputMode="numeric"
                  autoComplete="tel"
                  maxLength={countryCode === '+20' ? 11 : 15}
                  required
                />
              </PhoneNumberWrapper>
            </PhoneNumberSection>
            {phoneFormatError && <OTPErrorMessage>{phoneFormatError}</OTPErrorMessage>}
            {checkingPhone && phoneDigitsValid && (
              <PhoneCheckingText>Checking phone number…</PhoneCheckingText>
            )}
            {phoneUserExists && (
              <ExistingAccountMessage>
                {phoneUserExistsMessage || 'An account with this phone number already exists. Sign in instead.'}
                {' '}
                <LoginInlineLink to={loginHref}>
                  Sign In
                </LoginInlineLink>
              </ExistingAccountMessage>
            )}
            {otpError && <OTPErrorMessage>{otpError}</OTPErrorMessage>}
            <SubmitButton type="submit" disabled={!canSendOtp}>
              {sendingOTP ? 'Sending...' : checkingPhone && phoneDigitsValid ? 'Checking...' : 'Send Code'}
            </SubmitButton>
          </Form>
        )}

        {step === 'otp' && (
          <Form onSubmit={(e) => { e.preventDefault(); handleVerifyOTP(); }}>
            <OTPDigitRow>
              {otpDigits.map((digit, index) => (
                <OTPDigitInput
                  key={index}
                  ref={(el) => { otpInputRefs.current[index] = el; }}
                  type="text"
                  inputMode="numeric"
                  autoComplete={index === 0 ? 'one-time-code' : 'off'}
                  maxLength={index === 0 ? 6 : 1}
                  value={digit}
                  onChange={(e) => handleOtpDigitChange(index, e.target.value)}
                  onPaste={(e) => {
                    const pasted = e.clipboardData?.getData('text');
                    if (pasted && extractOtpFromPastedText(pasted).length === 6) {
                      e.preventDefault();
                      applyPastedOtp(pasted);
                    }
                  }}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  disabled={verifyingOTP}
                  aria-label={`Digit ${index + 1}`}
                />
              ))}
            </OTPDigitRow>
            <OtpHint>{otpEnterHint(otpDeliveryChannel)}</OtpHint>
            {otpError && <OTPErrorMessage>{otpError}</OTPErrorMessage>}
            <PasteOtpButton type="button" onClick={handlePasteOtp} disabled={verifyingOTP || sendingOTP}>
              Paste code
            </PasteOtpButton>
            {otpCountdown > 0 && (
              <OTPCountdown>Resend code in {otpCountdown}s</OTPCountdown>
            )}
            <VerifyOTPButton type="submit" disabled={verifyingOTP || otpDigits.join('').length !== 6}>
              {verifyingOTP ? 'Verifying...' : 'Verify Code'}
            </VerifyOTPButton>
            {otpCountdown === 0 && (
              <ResendOTPLink type="button" onClick={() => handleSendOTP()} disabled={sendingOTP}>
                {sendingOTP ? 'Sending...' : "Didn't get it? Resend"}
              </ResendOTPLink>
            )}
            {otpDeliveryChannel === 'whatsapp' && (otpVerifyFailed || otpCountdown === 0) && (
              <ResendOTPLink
                type="button"
                onClick={() => handleSendOTP({ preferSms: true })}
                disabled={sendingOTP || verifyingOTP}
              >
                Resend on SMS
              </ResendOTPLink>
            )}
            <BackLink type="button" onClick={goBackToPhone}>
              ← Change phone number
            </BackLink>
          </Form>
        )}

        {step === 'register' && (
          <Form onSubmit={handleCompleteRegistration}>
            <VerifiedPhoneRow>
              <span>✓</span> {verifiedPhoneNumber}
            </VerifiedPhoneRow>

            <Input
              type="text"
              name="full_name"
              placeholder={t('auth.registerFields.fullName')}
              value={formData.full_name}
              onChange={handleFormChange}
              autoComplete="name"
              required
            />

            <div>
              <Input
                type="email"
                name="email"
                placeholder={t('auth.registerFields.email')}
                value={formData.email}
                onChange={handleFormChange}
                autoComplete="email"
                inputMode="email"
                required
              />
              <FieldHint>{t('auth.registerFields.emailHint')}</FieldHint>
            </div>

            {!showReferralField && !formData.referral_code ? (
              <ReferralToggle type="button" onClick={() => setShowReferralField(true)}>
                Have a referral code?
              </ReferralToggle>
            ) : (
              <ReferralFieldGroup>
                <ReferralLabel htmlFor="referral_code">Referral code (optional)</ReferralLabel>
                <Input
                  id="referral_code"
                  type="text"
                  name="referral_code"
                  placeholder="Enter your friend's code"
                  value={formData.referral_code}
                  onChange={handleFormChange}
                  autoCapitalize="characters"
                  autoComplete="off"
                  maxLength={16}
                />
                <ReferralHint>
                  If a friend invited you, enter their code. They&apos;ll receive {REFERRAL_REWARD_EGP} EGP
                  in their wallet when you sign up.
                </ReferralHint>
              </ReferralFieldGroup>
            )}

            <div>
              <InputWrapper>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder={t('auth.registerFields.password')}
                  value={formData.password}
                  onChange={handleFormChange}
                  className={passwordError ? 'error' : ''}
                  autoComplete="new-password"
                  required
                />
                <ShowPasswordToggle
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </ShowPasswordToggle>
              </InputWrapper>
              {passwordError
                ? <ErrorMessage>{passwordError}</ErrorMessage>
                : <FieldHint>{t('auth.registerFields.passwordHint')}</FieldHint>}
            </div>

            <TextArea
              name="shipping_address"
              placeholder="Shipping Address (optional)"
              value={formData.shipping_address}
              onChange={handleFormChange}
              rows="3"
            />

            <LocationPicker
              value={formData.shipping_address}
              onChange={(address, coords) =>
                setFormData((prev) => ({
                  ...prev,
                  shipping_address: address,
                  ...(coords?.latitude != null && coords?.longitude != null
                    ? { latitude: coords.latitude, longitude: coords.longitude }
                    : {}),
                }))
              }
            />

            {userExists && (
              <ExistingAccountMessage>
                {userExistsMessage || 'An account with this phone number already exists. Sign in instead.'}
                {' '}
                <LoginInlineLink to={loginHref}>
                  Sign In
                </LoginInlineLink>
              </ExistingAccountMessage>
            )}

            {formError && <ErrorMessage role="alert">{formError}</ErrorMessage>}

            <TermsRow>
              <TermsCheckbox
                type="checkbox"
                id="acceptedTerms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
              />
              <TermsLabel htmlFor="acceptedTerms">
                I agree to the{' '}
                <TermsLink href="/terms" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </TermsLink>
                {' '}and{' '}
                <TermsLink href="/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </TermsLink>
                . Flea has zero tolerance for objectionable content or abusive users. Violations may result in content removal and account ejection within 24 hours.
              </TermsLabel>
            </TermsRow>

            <SubmitButton type="submit" disabled={loading || userExists || !acceptedTerms}>
              {loading
                ? 'Creating Account...'
                : userExists
                  ? 'Account Already Exists — Please Sign In'
                  : 'Complete Registration'}
            </SubmitButton>

            <BackLink type="button" onClick={goBackToPhone}>
              ← Use a different phone number
            </BackLink>

          </Form>
        )}

        <SignInFooter to={loginHref}>
          Already have an account? Sign In
        </SignInFooter>
      </FormContainer>
    </Container>
  );
};

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #a8e6cf 0%, #88d8a3 100%);
  padding: 20px;
`;

const FormContainer = styled.div`
  background: white;
  padding: 40px;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
`;

const Title = styled.h1`
  text-align: center;
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 28px;
  font-weight: bold;
`;

const Subtitle = styled.p`
  text-align: center;
  color: #7f8c8d;
  margin-bottom: 24px;
  font-size: 15px;
  line-height: 1.5;
`;

const ContinueBanner = styled.div`
  text-align: center;
  margin: -0.5rem 0 1.25rem;
  padding: 0.75rem 0.85rem;
  border-radius: 0.5rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
  font-size: 0.9rem;
  line-height: 1.45;
`;

const ContinueBannerLink = styled(Link)`
  color: #047857;
  font-weight: 700;
  text-decoration: underline;

  &:hover {
    color: #065f46;
  }
`;

const PhoneHighlight = styled.strong`
  color: #2c3e50;
  font-weight: 700;
`;

const OtpHint = styled.p`
  margin: 0 0 4px;
  color: #475569;
  font-size: 14px;
  line-height: 1.4;
  font-weight: 500;
  text-align: center;
`;

const PasteOtpButton = styled.button`
  background: none;
  border: none;
  color: #27ae60;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  padding: 4px;

  &:disabled {
    color: #95a5a6;
    cursor: not-allowed;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const Input = styled.input`
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3498db;
  }

  &.error {
    border-color: #e74c3c;
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 12px;
  margin-top: 5px;
  padding-left: 5px;
  white-space: pre-line;
`;

const FieldHint = styled.div`
  color: #64748b;
  font-size: 0.8rem;
  line-height: 1.45;
  margin-top: 5px;
  padding-left: 5px;
`;

const TermsRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  margin: 0.5rem 0 1rem;
`;

const TermsCheckbox = styled.input`
  margin-top: 0.2rem;
  width: 1.1rem;
  height: 1.1rem;
  flex-shrink: 0;
  accent-color: #0f766e;
`;

const TermsLabel = styled.label`
  font-size: 0.875rem;
  line-height: 1.45;
  color: #334155;
`;

const TermsLink = styled.a`
  color: #0f766e;
  font-weight: 600;
  text-decoration: underline;
`;

const ExistingAccountMessage = styled.div`
  background: #fff3cd;
  border: 2px solid #ffc107;
  color: #856404;
  padding: 15px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

const TextArea = styled.textarea`
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 16px;
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.3s ease;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const SubmitButton = styled.button`
  background: #27ae60;
  color: white;
  padding: 15px;
  border: none;
  border-radius: 10px;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover:not(:disabled) {
    background: #229954;
  }

  &:disabled {
    background: #95a5a6;
    cursor: not-allowed;
  }
`;

const SignInFooter = styled(Link)`
  display: block;
  background: none;
  border: none;
  color: #3498db;
  font-size: 15px;
  cursor: pointer;
  margin-top: 1.25rem;
  text-decoration: underline;
  width: 100%;
  text-align: center;
  box-sizing: border-box;

  &:hover {
    color: #2980b9;
  }
`;

const PhoneNumberSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PhoneNumberLabel = styled.label`
  display: block;
  color: #2c3e50;
  font-size: 14px;
  font-weight: 600;
`;

const PhoneNumberWrapper = styled.div`
  display: flex;
  gap: 10px;
  align-items: stretch;
`;

const CountryCodeSelect = styled.select`
  padding: 15px 10px;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 16px;
  background-color: white;
  min-width: 90px;
  max-width: 110px;

  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const PhoneInput = styled(Input)`
  flex: 1;
`;

const PhoneCheckingText = styled.div`
  color: #7f8c8d;
  font-size: 13px;
  text-align: center;
`;

const LoginInlineLink = styled(Link)`
  background: none;
  border: none;
  color: #3498db;
  font-size: inherit;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;

  &:hover {
    color: #2980b9;
  }
`;

const VerifiedPhoneRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 10px;
  color: #155724;
  font-size: 14px;
  font-weight: 600;
`;

const OTPDigitRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin: 8px 0;
`;

const OTPDigitInput = styled.input`
  width: 44px;
  height: 52px;
  border: 2px solid #3498db;
  border-radius: 10px;
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  background: white;

  &:focus {
    outline: none;
    border-color: #27ae60;
    box-shadow: 0 0 0 3px rgba(39, 174, 96, 0.15);
  }

  &:disabled {
    background: #f5f5f5;
  }
`;

const OTPErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 13px;
  text-align: center;
  font-weight: 500;
`;

const OTPCountdown = styled.div`
  color: #7f8c8d;
  font-size: 12px;
  text-align: center;
`;

const ResendOTPLink = styled.button`
  background: none;
  border: none;
  color: #3498db;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  width: 100%;
  padding: 5px;

  &:hover:not(:disabled) {
    color: #2980b9;
  }

  &:disabled {
    color: #95a5a6;
    cursor: not-allowed;
  }
`;

const VerifyOTPButton = styled(SubmitButton)`
  margin-top: 0;
`;

const ShowPasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #3498db;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px;

  &:hover {
    color: #2980b9;
  }
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: #7f8c8d;
  font-size: 14px;
  cursor: pointer;
  text-align: center;
  width: 100%;
  padding: 4px;

  &:hover {
    color: #2c3e50;
    text-decoration: underline;
  }
`;

const ReferralToggle = styled.button`
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  color: #3498db;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 14px;
  border-radius: 10px;
  cursor: pointer;
  text-align: center;
  width: 100%;

  &:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
  }
`;

const ReferralFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ReferralLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
`;

const ReferralHint = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: #7f8c8d;
`;

export default RegisterPage;
